import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Star } from "lucide-react";
import { toast } from "sonner";

const StarRow = ({ value, size = 16, onSelect }) => (
  <div className="inline-flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= Math.round(value);
      return (
        <button
          type="button"
          key={n}
          data-testid={onSelect ? `rate-star-${n}` : undefined}
          disabled={!onSelect}
          onClick={onSelect ? () => onSelect(n) : undefined}
          className={`transition-colors ${onSelect ? "cursor-pointer hover:scale-110" : "cursor-default"}`}
          aria-label={`${n} star`}
        >
          <Star
            size={size}
            strokeWidth={1.2}
            className={filled ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#5C4F4A]"}
          />
        </button>
      );
    })}
  </div>
);

export const Reviews = ({ productId }) => {
  const [data, setData] = useState({ reviews: [], count: 0, avg: 0 });
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    if (productId) load();
    // eslint-disable-next-line
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    if (rating < 1) return toast.error("Please pick a rating");
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, {
        name: name.trim(),
        rating,
        text: text.trim(),
      });
      setName("");
      setRating(0);
      setText("");
      toast.success("Review posted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-section" className="mt-16 pt-12 border-t border-[#2D2622]">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] tracking-overline uppercase font-bold text-[#D4AF37] mb-2">
            Reviews
          </p>
          <h3 className="font-serif-display text-3xl md:text-4xl text-[#F4EAD9] leading-tight">
            What others are wearing.
          </h3>
        </div>
        {data.count > 0 && (
          <div data-testid="reviews-summary" className="text-right">
            <StarRow value={data.avg} size={18} />
            <p className="mt-1 text-sm text-[#B0A496]">
              <b className="text-[#D4AF37]">{data.avg.toFixed(1)}</b> · {data.count} review
              {data.count === 1 ? "" : "s"}
            </p>
          </div>
        )}
      </div>

      {/* List */}
      <div className="mt-8 space-y-6">
        {data.reviews.length === 0 && (
          <p data-testid="reviews-empty" className="text-sm text-[#B0A496]">
            No reviews yet. Be the first to leave one.
          </p>
        )}
        {data.reviews.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className="pb-6 border-b border-[#2D2622]/60">
            <div className="flex items-center gap-3 mb-2">
              <StarRow value={r.rating} size={14} />
              <p className="font-serif-display text-lg text-[#F4EAD9]">{r.name}</p>
              <p className="text-[10px] tracking-overline uppercase text-[#5C4F4A] ml-auto">
                {r.created_at?.slice(0, 10)}
              </p>
            </div>
            {r.text && <p className="text-sm text-[#B0A496] leading-relaxed">{r.text}</p>}
          </div>
        ))}
      </div>

      {/* Form */}
      <form data-testid="review-form" onSubmit={submit} className="mt-10 grid gap-4">
        <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496]">
          Write your review
        </p>
        <div className="flex items-center gap-3">
          <span className="text-[10px] tracking-overline uppercase text-[#B0A496]">Your rating</span>
          <StarRow value={rating} onSelect={setRating} size={20} />
        </div>
        <input
          data-testid="review-name"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-transparent border border-[#2D2622] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4EAD9] placeholder:text-[#5C4F4A]"
        />
        <textarea
          data-testid="review-text"
          rows={3}
          placeholder="How does it wear? (optional)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="bg-transparent border border-[#2D2622] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4EAD9] placeholder:text-[#5C4F4A]"
        />
        <button
          data-testid="review-submit"
          disabled={submitting}
          className="justify-self-start bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase font-bold transition-colors disabled:opacity-70"
        >
          {submitting ? "Posting…" : "Post review"}
        </button>
      </form>
    </div>
  );
};

export default Reviews;
